import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ROLE_ADD_EDIT_SCHEMA } from './RoleAddEditSchema';
import BaseOverlay from '../../../components/layout/BaseOverlay.jsx';
import BaseButton from '../../form/BaseButton.jsx';
import BaseInput from '../../../components/form/BaseInput.jsx';
import BaseDropdown from '../../form/BaseDropdown.jsx';
import BaseCheckboxTheme from '../../form/BaseCheckboxTheme.jsx';
import {
    useAddRoleMutation,
    useEditRoleMutation,
    useGetRolePermissionsQuery,
    useGetRoleQuery
} from '../../../api/roles.js';
import ThreeDotsLoader from '../../../components/misc/ThreeDotsLoader.jsx';
import IconOrganize from '../../images/IconOrganize.jsx';
import { Carousel } from 'primereact/carousel';
import BaseCard from '../BaseCard.jsx';
import { getInitials } from '../../../utils/getInitials.js';
import RolePermissions from './RolePermissions';
import useCurrentUser from '../../../hooks/useCurrentUser.js';
import { useErrorToast } from '../../../hooks/useToast.jsx';

const RoleAddEditForm = ({ user, roleId, showOverlay, setShowOverlay, rolesTypesList, roleTypesLoading }) => {
    const { separatePermissions } = useCurrentUser();
    const {
        data: permissionsData,
        error: permissionsDataError,
        isLoading: permissionsDataLoading
    } = useGetRolePermissionsQuery();

    const permissionsList = permissionsData?.response || [];

    const methods = useForm({
        resolver: yupResolver(ROLE_ADD_EDIT_SCHEMA),
        defaultValues: {
            roleName: '',
            roleDescription: '',
            roleType: '',
            selectedOrganizations: {},
            selectedOrganization: '',
            permissions: {}
        }
    });
    const {
        handleSubmit,
        reset,
        control,
        setValue,
        watch,
        formState: { errors }
    } = methods;

    const [addRole, { isLoading: isLoadingAdding }] = useAddRoleMutation();
    const [editRole, { isLoading: isLoadingEditing }] = useEditRoleMutation();

    const {
        data: roleData,
        error: roleError,
        isLoading: roleLoading
    } = useGetRoleQuery(roleId, {
        skip: !roleId
    });

    const role = roleData?.response?.role || null;

    const isEditMode = !!roleId;
    const selectedRoleType = watch('roleType');
    const selectedOrganizations = watch('selectedOrganizations');
    const selectedOrganizationId = watch('selectedOrganization');

    const mapPermissions = (permissionsArray) => {
        const permissionsObj = {};

        permissionsArray.forEach((perm) => {
            if (perm.organization_id) {
                if (!permissionsObj[perm.organization_id]) {
                    permissionsObj[perm.organization_id] = {};
                }
                if (!permissionsObj[perm.organization_id][perm.permission]) {
                    permissionsObj[perm.organization_id][perm.permission] = {};
                }
                permissionsObj[perm.organization_id][perm.permission] = {
                    read: perm.read,
                    create: perm.create,
                    update: perm.update,
                    delete: perm.delete
                };
            } else {
                if (!permissionsObj['general']) {
                    permissionsObj['general'] = {};
                }
                if (!permissionsObj['general'][perm.permission]) {
                    permissionsObj['general'][perm.permission] = {};
                }
                permissionsObj['general'][perm.permission] = {
                    read: perm.read,
                    create: perm.create,
                    update: perm.update,
                    delete: perm.delete
                };
            }
        });

        return permissionsObj;
    };

    useEffect(() => {
        if (showOverlay) {
            const orgPermissions = {};
            let defaultOrganizationId = '';

            if (isEditMode && role?.permissions) {
                role.permissions.forEach((perm) => {
                    if (perm.organization_id) {
                        orgPermissions[perm.organization_id] = true;
                        if (!defaultOrganizationId) {
                            defaultOrganizationId = perm.organization_id;
                        }
                    }
                });
            }

            reset({
                roleName: isEditMode && role?.name ? role.name : '',
                roleDescription: isEditMode && role?.description ? role.description : '',
                roleType: isEditMode && role?.type ? role.type : '',
                selectedOrganizations: orgPermissions,
                selectedOrganization: defaultOrganizationId,
                permissions: isEditMode && role?.permissions ? mapPermissions(role.permissions) : {}
            });
        }
    }, [isEditMode, role, reset, showOverlay]);

    useEffect(() => {
        if (!isEditMode) {
            setValue('selectedOrganizations', {});
            setValue('selectedOrganization', '');
            setValue('permissions', {});
        }
    }, [isEditMode, selectedRoleType, setValue]);

    const closeOverlay = () => {
        setShowOverlay(false);
    };

    const onSubmit = async (data) => {
        const formattedData = {
            name: data.roleName,
            description: data.roleDescription,
            type: data.roleType,
            permissions: {
                general: Object.fromEntries(
                    Object.entries(data.permissions['general']).filter(
                        ([key]) =>
                            data.roleType === 'admin' ||
                            (data.roleType === 'user' && !permissionsList?.admin_permissions.includes(key))
                    )
                )
            }
        };

        const filterEmptyPermissions = (permissions) => {
            const filteredPermissions = {};

            Object.keys(permissions).forEach((category) => {
                const actions = permissions[category];
                const hasValidPermissions = Object.values(actions).some((value) => value);

                if (hasValidPermissions) {
                    filteredPermissions[category] = actions;
                }
            });

            return filteredPermissions;
        };

        if (data.roleType === 'admin') {
            Object.keys(data.selectedOrganizations).forEach((orgId) => {
                if (data.selectedOrganizations[orgId]) {
                    const orgPermissions = data.permissions[orgId] || {};
                    const filteredPermissions = filterEmptyPermissions(orgPermissions);

                    if (Object.keys(filteredPermissions).length > 0) {
                        formattedData.permissions[orgId] = filteredPermissions;
                    }
                }
            });
        } else if (data.roleType === 'user' && data.selectedOrganization) {
            const orgPermissions = data.permissions[data.selectedOrganization] || {};
            let filteredPermissions = filterEmptyPermissions(orgPermissions);

            filteredPermissions = Object.fromEntries(
                Object.entries(filteredPermissions).filter(([key]) => !permissionsList?.admin_permissions.includes(key))
            );

            if (Object.keys(filteredPermissions).length > 0) {
                formattedData.permissions[data.selectedOrganization] = filteredPermissions;
            }
        }

        try {
            if (isEditMode) {
                await editRole({ id: roleId, data: formattedData });
            } else {
                await addRole(formattedData);
            }
            closeOverlay();
        } catch (error) {
            console.error('Error during mutation:', error);
            useErrorToast('Role saving failed');
        }
    };

    if (permissionsDataLoading) {
        return <ThreeDotsLoader className='mt-3' />;
    }

    if (permissionsDataError) {
        return <div className='text-theme1-primary mt-3'>Error loading permissions</div>;
    }

    return (
        <BaseOverlay show={showOverlay} className='overflow-auto h-[100dvh] rounded-none'>
            <FormProvider {...methods}>
                <div className='min-h-96 max-w-3xl w-full mx-auto px-4'>
                    <div className='flex justify-end py-4'>
                        <div className='text-theme1-primary font-bold cursor-pointer' onClick={closeOverlay}>
                            Close
                        </div>
                    </div>

                    <div className='text-theme1-primary font-bold text-[22px] mb-3'>
                        {isEditMode ? 'Edit Role' : 'New Role Creation'}
                    </div>

                    <BaseInput
                        name='roleName'
                        label='Role Name'
                        placeholder={isEditMode ? role?.name : 'New role name'}
                        control={control}
                        inputClassName='border-transparent border-b border-b-theme1-cardStroke rounded-none px-[0] py-[5px] outline-none'
                        labelClassName='sr-only'
                        errors={errors.roleName ? [errors.roleName.message] : []}
                        disabled={isEditMode && roleLoading}
                    />

                    <BaseInput
                        name='roleDescription'
                        label='Role Description'
                        placeholder={isEditMode ? role?.description : 'New role description'}
                        control={control}
                        className='mt-4'
                        inputClassName='border-transparent border-b border-b-theme1-cardStroke rounded-none px-[0] py-[5px] outline-none'
                        labelClassName='sr-only'
                        errors={errors.roleDescription ? [errors.roleDescription.message] : []}
                        disabled={isEditMode && roleLoading}
                    />

                    <div className='text-theme1-text1 font-bold text-[15px] mt-7 mb-3'>Role Type Selection</div>

                    {roleTypesLoading ? (
                        <ThreeDotsLoader />
                    ) : (
                        <BaseDropdown
                            name='roleType'
                            label={false}
                            placeholder='Select a role type'
                            control={control}
                            options={rolesTypesList}
                            errors={errors.roleType ? [errors.roleType.message] : []}
                            filter={false}
                            disabled={isEditMode && roleLoading}
                        />
                    )}

                    {selectedRoleType !== '' && (
                        <RolePermissions
                            editMode={isEditMode}
                            sectionText={'General Permissions'}
                            organizationId={'general'}
                            permissionsList={permissionsList?.general_permissions}
                            roleType={selectedRoleType}
                            adminPermissions={permissionsList?.admin_permissions}
                            maxPermissions={separatePermissions['general']}
                        />
                    )}

                    {selectedRoleType === 'admin' && (!isEditMode || (isEditMode && roleData)) ? (
                        <>
                            <div className='flex mt-7 mb-3 gap-4 items-center'>
                                <div className='bg-theme1-gold p-4 rounded-2xl w-[60px] h-[60px] flex items-center justify-center'>
                                    <IconOrganize className='stroke-theme1-white' />
                                </div>
                                <div className='text-theme1-text1 font-bold text-lg whitespace-pre'>Organizations</div>
                            </div>
                            <Carousel
                                value={user.organizations}
                                numScroll={1}
                                numVisible={1}
                                itemTemplate={(organization) => {
                                    const hasPermissions =
                                        isEditMode &&
                                        role?.permissions?.some((perm) => perm.organization_id === organization.id);
                                    const isChecked =
                                        hasPermissions || selectedOrganizations?.[organization.id] || false;

                                    return (
                                        <BaseCard key={organization.id} className='mt-4 shadow-none'>
                                            <div className='flex justify-between items-center'>
                                                <div className='rounded-full w-[60px] h-[60px] overflow-hidden mr-[16px]'>
                                                    <div className='w-full h-full bg-theme1-pale flex items-center justify-center'>
                                                        <div className='text-theme1-primary font-bold text-[28px]'>
                                                            {getInitials(organization.name)}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='text-theme1-primary font-bold text-xl'>
                                                    {organization.name}
                                                </div>
                                                <BaseCheckboxTheme
                                                    className='w-auto ml-auto'
                                                    control={control}
                                                    name={`selectedOrganizations.${organization.id}`}
                                                    label={false}
                                                />
                                            </div>
                                            {isChecked && (
                                                <RolePermissions
                                                    editMode={isEditMode}
                                                    sectionText={'Organization Permissions'}
                                                    organizationId={organization.id}
                                                    permissionsList={permissionsList?.organization_permissions}
                                                    roleType={selectedRoleType}
                                                    adminPermissions={permissionsList?.admin_permissions}
                                                    maxPermissions={separatePermissions[organization.id]}
                                                />
                                            )}
                                        </BaseCard>
                                    );
                                }}
                                showNavigators={false}
                                showIndicators
                            />
                        </>
                    ) : selectedRoleType === 'user' ? (
                        <>
                            <div className='flex mt-7 mb-3 gap-4 items-center'>
                                <div className='bg-theme1-gold p-4 rounded-2xl w-[60px] h-[60px] flex items-center justify-center'>
                                    <IconOrganize className='stroke-theme1-white' />
                                </div>
                                <div className='text-theme1-text1 font-bold text-lg whitespace-pre'>Organization</div>
                            </div>
                            <BaseDropdown
                                name='selectedOrganization'
                                label={false}
                                control={control}
                                placeholder='Select an organization'
                                options={user.organizations.map((org) => ({ label: org.name, value: org.id }))}
                                filter={false}
                                className='mt-6'
                            />
                            {selectedOrganizationId && (
                                <RolePermissions
                                    editMode={isEditMode}
                                    sectionText={'Organization Permissions'}
                                    organizationId={selectedOrganizationId}
                                    key={selectedOrganizationId}
                                    permissionsList={permissionsList?.organization_permissions}
                                    roleType={selectedRoleType}
                                    adminPermissions={permissionsList?.admin_permissions}
                                    maxPermissions={separatePermissions[selectedOrganizationId]}
                                />
                            )}
                        </>
                    ) : null}

                    {errors.permissions && (
                        <div className='text-theme1-error mt-4'>
                            You must select at least one organization and have at least one permission for the selected
                            organization(s).
                        </div>
                    )}

                    <BaseButton
                        className='mt-4 mb-7 btn-red-outline'
                        textValue='Save changes'
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoadingAdding || isLoadingEditing}
                    />
                </div>
            </FormProvider>
        </BaseOverlay>
    );
};

export default RoleAddEditForm;
